import type { Statistica } from "@flow";
import { type CommonData, Country } from "../../../../types/common";
import { formatAmount } from "../../../../utils/helperFunctions";
import { NormalInfoWrapper } from "../../../tasks/styles";
import { CopyLink } from "../../copyLink";
import { ModalLink } from "../../modalLink";

interface OfferDetailsProps {
  data: CommonData;
  flowId: string;
  handleModalClick: () => void;
}

interface Detail {
  label: string;
  value: any;
}

function getIndexOfLastRow(details: Detail[], rejectedPolicies: any) {
  if (rejectedPolicies === null && rejectedPolicies.length <= 0) {
    return [];
  }
  let lastValidRow = -1;
  for (let i = 0; i < details.length; i++) {
    if (details[i].value != null) {
      lastValidRow = i;
    }
  }
  return [lastValidRow];
}
export const ApplicationtRow = ({
  data,
  flowId,
  handleModalClick
}: OfferDetailsProps) => {
  const country = data?.country;
  const isFi = country === Country.FI;
  // Finland sources the same fields from its own statistica endpoints.
  const external = isFi
    ? data?.integrations?.statistica?.FI_External
    : data?.integrations?.statistica?.SE_External;
  // FI purpose comes from the application itself; the FI statistica model does
  // not echo it back the way SE_External does.
  const purpose = isFi
    ? data?.agentApiApplication?.application?.purpose
    : external?.variables?.purpose;
  // FI has no nbrApplications statistica variable; the count comes from the
  // count-previous-applications task (loanApplication.mainApplicant).
  const previousApplications = isFi
    ? data?.loanApplication?.mainApplicant?.totalNrOfApplications?.count
    : data?.integrations?.statistica?.SE_Internal?.variables?.nbrApplications;
  const showKyc = !!data?.integrations?.cm1;
  const appliedRepaymentTime =
    data.agentApiApplication.application?.appliedRepaymentTime || 0;
  const process = data.agentApiApplication?.process;

  // Rejection reasons from Statistica
  const statistica: Statistica = data?.integrations?.statistica || {};
  const policiesByStatistica = Object.values(statistica).map(
    ({ policyResults = {} }) => policyResults
  );
  const rejectedPolicies = policiesByStatistica.flatMap((policyResults) => {
    // Check if policyResults is an array (new structure)
    if (Array.isArray(policyResults)) {
      return policyResults
        .filter((policy) => policy.status === "Red")
        .map((policy) => policy.description?.trim());
    }

    // Handle old structure (object with key-value pairs)
    return Object.entries(policyResults)
      .filter(([, result]) => result === "Red")
      .map(([description]) => description?.trim());
  });
  const rejectReasons = rejectedPolicies.map((rule) => `• ${rule}`);
  const sortedRejectReasons = rejectReasons.sort((a, b) => a.localeCompare(b));
  const paymentOption =
    process === "sales-finance-se"
      ? appliedRepaymentTime === 0
        ? "Invoice"
        : "Fixed"
      : null;
  const appliedAmount =
    process !== "card-se"
      ? formatAmount(
          data?.agentApiApplication?.application?.appliedAmount,
          country
        )
      : null;
  const appliedRefinanceAmount =
    process !== "card-se"
      ? formatAmount(
          data?.agentApiApplication?.application?.refinanceAmount,
          country
        )
      : null;

  const applicationDetails = [
    { label: "FlowId", value: <CopyLink title="Flow ID" value={flowId} /> },
    { label: "Agent ID", value: data?.agentApiApplication?.agentId },
    {
      label: "Campaign ID",
      value: data?.agentApiApplication?.application?.campaignId
    },
    {
      label: "Previous applications",
      value: previousApplications
    },
    { label: "Purpose", value: purpose },
    { label: "Applied amount", value: appliedAmount },
    { label: "Applied refinance amount", value: appliedRefinanceAmount },
    { label: "Payment option", value: paymentOption }
  ];

  if (showKyc) {
    applicationDetails.splice(3, 0, {
      label: "KYC",
      value: <ModalLink title="KYC" handleOnClick={handleModalClick} />
    });
  }

  const lastIndexes = getIndexOfLastRow(applicationDetails, rejectedPolicies);
  return (
    <>
      <NormalInfoWrapper $header>Application Details</NormalInfoWrapper>
      {applicationDetails.map((detail, index) => {
        if (detail.value === null || detail.value === undefined) {
          return;
        }
        return (
          <NormalInfoWrapper
            key={`application - ${detail.label}`}
            $isLast={
              lastIndexes.includes(index) && rejectedPolicies.length <= 0
            }
          >
            <div>{detail.label}</div>
            <div>{detail.value}</div>
          </NormalInfoWrapper>
        );
      })}
      {rejectedPolicies.length > 0 && (
        <>
          <NormalInfoWrapper $isLast>
            Rejection reasons from Statistica
          </NormalInfoWrapper>
          <NormalInfoWrapper $isLast>
            <div>
              {sortedRejectReasons.map((reason, index) => (
                <div
                  style={{
                    fontSize: "13px"
                  }}
                  key={`reject reason${reason}`}
                >
                  {reason}
                </div>
              ))}
            </div>
          </NormalInfoWrapper>
        </>
      )}
    </>
  );
};
